import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { frenchDateOnly, frenchMonth } from '../../../../variables/functions';
import { displayNumbers } from '../../../../variables/numbersFunctions';

/**
 * Export data to Excel with formatting
 * @param {Array} data - Array of data objects
 * @param {String} fileName - Name of the file to export
 * @param {String} sheetName - Name of the worksheet
 * @param {Object} options - Additional options for formatting
 */
export const exportToExcel = (data, fileName = 'export', sheetName = 'Sheet1', options = {}) => {
    try {
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);

        // Auto-size columns
        const colWidths = [];
        const keys = Object.keys(data[0] || {});

        keys.forEach((key, i) => {
            const maxLength = Math.max(
                key.length,
                ...data.map(row => String(row[key] || '').length)
            );
            colWidths[i] = { wch: Math.min(maxLength + 2, 50) };
        });

        ws['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        // Write file
        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return true;
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        return false;
    }
};

/**
 * Export GUM production report to Excel with proper formatting
 */
export const exportGUMProductionToExcel = (jours, mois, bureau = null) => {
    try {
        // Calculate totals
        const totals = {
            totalMotoPuissance0: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance0, 0),
            totalMontantPuissance0: jours.reduce((sum, jour) => sum + jour.montantPuissance0, 0),
            totalMotoPuissance1: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance1, 0),
            totalMontantPuissance1: jours.reduce((sum, jour) => sum + jour.montantPuissance1, 0),
            totalMotoPuissance2: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance2, 0),
            totalMontantPuissance2: jours.reduce((sum, jour) => sum + jour.montantPuissance2, 0),
            totalMotoPuissance3: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance3, 0),
            totalMontantPuissance3: jours.reduce((sum, jour) => sum + jour.montantPuissance3, 0),
            totalMotoTotal: jours.reduce((sum, jour) => sum + jour.nombreMotoTotal, 0),
            totalPrimeNette: jours.reduce((sum, jour) => sum + jour.primeNette, 0),
            totalTaxes: jours.reduce((sum, jour) => sum + jour.taxes, 0),
            totalPrimeTotale: jours.reduce((sum, jour) => sum + jour.primeTotale, 0),
            totalTimbre: jours.reduce((sum, jour) => sum + jour.timbre, 0),
            totalMontantVerse: jours.reduce((sum, jour) => sum + jour.montantVerse, 0),
            totalResteAReverser: jours.reduce((sum, jour) => sum + jour.primeTotale, 0),
        };

        // Prepare data for Excel
        const excelData = jours.map(jour => ({
            'DATE': frenchDateOnly(jour.date),
            'BUREAUX': jour.bureau,
            'PUISSANCE 0 - NOMBRE': jour.nombreMotoPuissance0,
            'PUISSANCE 0 - MONTANT': jour.montantPuissance0,
            'PUISSANCE 1 - NOMBRE': jour.nombreMotoPuissance1,
            'PUISSANCE 1 - MONTANT': jour.montantPuissance1,
            'PUISSANCE 2 - NOMBRE': jour.nombreMotoPuissance2,
            'PUISSANCE 2 - MONTANT': jour.montantPuissance2,
            'PUISSANCE 3 - NOMBRE': jour.nombreMotoPuissance3,
            'PUISSANCE 3 - MONTANT': jour.montantPuissance3,
            'NBRE DE MOTOS': jour.nombreMotoTotal,
            'PRIME NETTE': jour.primeNette,
            'TAXES': jour.taxes,
            'PRIME TTC': jour.primeTotale,
            'CUMUL': jour.cumul,
            'TIMBRE': jour.timbre,
            'MONTANT VERSE': jour.montantVerse,
            'RESTE A REVERSER': jour.montantRestant,
            'OBSERVATIONS': jour.observations || ''
        }));

        // Add totals row
        excelData.push({
            'DATE': 'TOTAL',
            'BUREAUX': '',
            'PUISSANCE 0 - NOMBRE': totals.totalMotoPuissance0,
            'PUISSANCE 0 - MONTANT': totals.totalMontantPuissance0,
            'PUISSANCE 1 - NOMBRE': totals.totalMotoPuissance1,
            'PUISSANCE 1 - MONTANT': totals.totalMontantPuissance1,
            'PUISSANCE 2 - NOMBRE': totals.totalMotoPuissance2,
            'PUISSANCE 2 - MONTANT': totals.totalMontantPuissance2,
            'PUISSANCE 3 - NOMBRE': totals.totalMotoPuissance3,
            'PUISSANCE 3 - MONTANT': totals.totalMontantPuissance3,
            'NBRE DE MOTOS': totals.totalMotoTotal,
            'PRIME NETTE': totals.totalPrimeNette,
            'TAXES': totals.totalTaxes,
            'PRIME TTC': totals.totalPrimeTotale,
            'CUMUL': '',
            'TIMBRE': totals.totalTimbre,
            'MONTANT VERSE': totals.totalMontantVerse,
            'RESTE A REVERSER': totals.totalResteAReverser,
            'OBSERVATIONS': ''
        });

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        ws['!cols'] = [
            { wch: 12 }, // DATE
            { wch: 20 }, // BUREAUX
            { wch: 10 }, // P0 NOMBRE
            { wch: 12 }, // P0 MONTANT
            { wch: 10 }, // P1 NOMBRE
            { wch: 12 }, // P1 MONTANT
            { wch: 10 }, // P2 NOMBRE
            { wch: 12 }, // P2 MONTANT
            { wch: 10 }, // P3 NOMBRE
            { wch: 12 }, // P3 MONTANT
            { wch: 12 }, // NBRE MOTOS
            { wch: 12 }, // PRIME NETTE
            { wch: 10 }, // TAXES
            { wch: 12 }, // PRIME TTC
            { wch: 12 }, // CUMUL
            { wch: 10 }, // TIMBRE
            { wch: 14 }, // MONTANT VERSE
            { wch: 14 }, // RESTE A REVERSER
            { wch: 20 }  // OBSERVATIONS
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Production GUM');

        const monthName = frenchMonth(mois.split('-')[1]);
        const year = mois.split('-')[0];
        const bureauName = bureau ? `_${bureau}` : '_National';
        const fileName = `Etat_Global_Production_GUM${bureauName}_${monthName}_${year}`;

        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return true;
    } catch (error) {
        console.error('Error exporting GUM production to Excel:', error);
        return false;
    }
};

/**
 * Export Emissions Nettes to Excel
 */
export const exportEmissionsNettesToExcel = (emissions, categorie, exercice) => {
    try {
        const excelData = emissions.map(emission => ({
            'COMPAGNIE': emission.compagnie,
            'PRIME EMISE': emission.primeEmise,
            'ANNULATION': emission.annulation,
            'PRIME NETTE EMISE': emission.primeNetteEmise,
            'CESSION LEGALE': emission.cessionLegale,
            'CESSION FACULTATIVE': emission.cessionFacultative,
            'CESSION TOTALE': emission.cessionTotale,
            'PRIME NETTE CEDEE': emission.primeNetteCedee,
            'TAUX DE CESSION': emission.tauxCession ? `${emission.tauxCession.toFixed(2)}%` : '0%'
        }));

        // Calculate totals
        const totals = {
            'COMPAGNIE': 'TOTAL',
            'PRIME EMISE': emissions.reduce((sum, e) => sum + e.primeEmise, 0),
            'ANNULATION': emissions.reduce((sum, e) => sum + e.annulation, 0),
            'PRIME NETTE EMISE': emissions.reduce((sum, e) => sum + e.primeNetteEmise, 0),
            'CESSION LEGALE': emissions.reduce((sum, e) => sum + e.cessionLegale, 0),
            'CESSION FACULTATIVE': emissions.reduce((sum, e) => sum + e.cessionFacultative, 0),
            'CESSION TOTALE': emissions.reduce((sum, e) => sum + e.cessionTotale, 0),
            'PRIME NETTE CEDEE': emissions.reduce((sum, e) => sum + e.primeNetteCedee, 0),
            'TAUX DE CESSION': ''
        };

        excelData.push(totals);

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        ws['!cols'] = [
            { wch: 30 }, // COMPAGNIE
            { wch: 15 }, // PRIME EMISE
            { wch: 15 }, // ANNULATION
            { wch: 18 }, // PRIME NETTE EMISE
            { wch: 15 }, // CESSION LEGALE
            { wch: 18 }, // CESSION FACULTATIVE
            { wch: 15 }, // CESSION TOTALE
            { wch: 18 }, // PRIME NETTE CEDEE
            { wch: 15 }  // TAUX DE CESSION
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Emissions Nettes');

        const fileName = `Emissions_Nettes_Cedees_${categorie}_${exercice}`;
        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return true;
    } catch (error) {
        console.error('Error exporting emissions to Excel:', error);
        return false;
    }
};

/**
 * Export component to PDF using html2canvas
 * @param {React.RefObject} componentRef - Reference to the component to export
 * @param {String} fileName - Name of the PDF file
 * @param {Object} options - PDF options (orientation, format, etc.)
 */
export const exportToPDF = async (componentRef, fileName = 'export', options = {}) => {
    try {
        const {
            orientation = 'landscape',
            format = 'a4',
            quality = 0.95,
            scale = 2
        } = options;

        const element = componentRef.current;
        if (!element) {
            throw new Error('Component reference not found');
        }

        // Capture the component as canvas
        const canvas = await html2canvas(element, {
            scale: scale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png', quality);

        // Create PDF
        const pdf = new jsPDF({
            orientation: orientation,
            unit: 'mm',
            format: format
        });

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Add image to PDF
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        // Save PDF
        pdf.save(`${fileName}.pdf`);

        return true;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        return false;
    }
};

/**
 * Export GUM Production to PDF using jsPDF AutoTable
 */
export const exportGUMProductionToPDF = (jours, mois, bureau = null) => {
    try {
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        // Add title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('ETAT GLOBAL DE PRODUCTION GUM', pdf.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const bureauText = bureau || 'LOME-KPALIME-ATAKPAME-SOKODE-KARA-DAPAONG';
        pdf.text(bureauText, pdf.internal.pageSize.getWidth() / 2, 22, { align: 'center' });

        const monthName = frenchMonth(mois.split('-')[1]);
        const year = mois.split('-')[0];
        pdf.text(`MOIS: ${monthName.toUpperCase()} ${year}`, pdf.internal.pageSize.getWidth() / 2, 28, { align: 'center' });

        // Calculate totals
        const totals = {
            totalMotoPuissance0: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance0, 0),
            totalMontantPuissance0: jours.reduce((sum, jour) => sum + jour.montantPuissance0, 0),
            totalMotoPuissance1: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance1, 0),
            totalMontantPuissance1: jours.reduce((sum, jour) => sum + jour.montantPuissance1, 0),
            totalMotoPuissance2: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance2, 0),
            totalMontantPuissance2: jours.reduce((sum, jour) => sum + jour.montantPuissance2, 0),
            totalMotoPuissance3: jours.reduce((sum, jour) => sum + jour.nombreMotoPuissance3, 0),
            totalMontantPuissance3: jours.reduce((sum, jour) => sum + jour.montantPuissance3, 0),
            totalMotoTotal: jours.reduce((sum, jour) => sum + jour.nombreMotoTotal, 0),
            totalPrimeNette: jours.reduce((sum, jour) => sum + jour.primeNette, 0),
            totalTaxes: jours.reduce((sum, jour) => sum + jour.taxes, 0),
            totalPrimeTotale: jours.reduce((sum, jour) => sum + jour.primeTotale, 0),
            totalTimbre: jours.reduce((sum, jour) => sum + jour.timbre, 0),
            totalMontantVerse: jours.reduce((sum, jour) => sum + jour.montantVerse, 0),
            totalResteAReverser: jours.reduce((sum, jour) => sum + jour.primeTotale, 0),
        };

        // Prepare table data
        const tableData = jours.map(jour => [
            frenchDateOnly(jour.date),
            jour.bureau,
            displayNumbers(jour.nombreMotoPuissance0),
            displayNumbers(jour.montantPuissance0),
            displayNumbers(jour.nombreMotoPuissance1),
            displayNumbers(jour.montantPuissance1),
            displayNumbers(jour.nombreMotoPuissance2),
            displayNumbers(jour.montantPuissance2),
            displayNumbers(jour.nombreMotoPuissance3),
            displayNumbers(jour.montantPuissance3),
            displayNumbers(jour.nombreMotoTotal),
            displayNumbers(jour.primeNette),
            displayNumbers(jour.taxes),
            displayNumbers(jour.primeTotale),
            displayNumbers(jour.timbre),
            displayNumbers(jour.montantVerse),
            displayNumbers(jour.montantRestant)
        ]);

        // Add totals row
        tableData.push([
            'TOTAL',
            '',
            displayNumbers(totals.totalMotoPuissance0),
            displayNumbers(totals.totalMontantPuissance0),
            displayNumbers(totals.totalMotoPuissance1),
            displayNumbers(totals.totalMontantPuissance1),
            displayNumbers(totals.totalMotoPuissance2),
            displayNumbers(totals.totalMontantPuissance2),
            displayNumbers(totals.totalMotoPuissance3),
            displayNumbers(totals.totalMontantPuissance3),
            displayNumbers(totals.totalMotoTotal),
            displayNumbers(totals.totalPrimeNette),
            displayNumbers(totals.totalTaxes),
            displayNumbers(totals.totalPrimeTotale),
            displayNumbers(totals.totalTimbre),
            displayNumbers(totals.totalMontantVerse),
            displayNumbers(totals.totalResteAReverser)
        ]);

        // Add table
        pdf.autoTable({
            head: [[
                'DATE',
                'BUREAUX',
                { content: 'P0\nNBRE', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P0\nMONT', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P1\nNBRE', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P1\nMONT', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P2\nNBRE', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P2\nMONT', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P3\nNBRE', colSpan: 1, styles: { halign: 'center' } },
                { content: 'P3\nMONT', colSpan: 1, styles: { halign: 'center' } },
                'MOTOS',
                'P.NETTE',
                'TAXES',
                'P.TTC',
                'TIMBRE',
                'VERSE',
                'RESTE'
            ]],
            body: tableData,
            startY: 35,
            styles: {
                fontSize: 7,
                cellPadding: 1
            },
            headStyles: {
                fillColor: [36, 33, 102],
                textColor: 255,
                fontSize: 7,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 15 },
                1: { cellWidth: 20 },
                2: { cellWidth: 10, halign: 'right' },
                3: { cellWidth: 12, halign: 'right' },
                4: { cellWidth: 10, halign: 'right' },
                5: { cellWidth: 12, halign: 'right' },
                6: { cellWidth: 10, halign: 'right' },
                7: { cellWidth: 12, halign: 'right' },
                8: { cellWidth: 10, halign: 'right' },
                9: { cellWidth: 12, halign: 'right' },
                10: { cellWidth: 12, halign: 'right' },
                11: { cellWidth: 14, halign: 'right' },
                12: { cellWidth: 12, halign: 'right' },
                13: { cellWidth: 14, halign: 'right' },
                14: { cellWidth: 12, halign: 'right' },
                15: { cellWidth: 14, halign: 'right' },
                16: { cellWidth: 14, halign: 'right' }
            },
            didParseCell: function (data) {
                // Make total row bold
                if (data.row.index === tableData.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });

        const fileName = `Etat_Production_GUM_${monthName}_${year}${bureau ? `_${bureau}` : '_National'}`;
        pdf.save(`${fileName}.pdf`);

        return true;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        return false;
    }
};

/**
 * Export Emissions Nettes to PDF
 */
export const exportEmissionsNettesToPDF = (emissions, categorie, exercice) => {
    try {
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        // Add title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('EMISSIONS NETTES CEDEES', pdf.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`CATEGORIE: ${categorie} - EXERCICE: ${exercice}`, pdf.internal.pageSize.getWidth() / 2, 22, { align: 'center' });

        // Calculate totals
        const totals = {
            primeEmise: emissions.reduce((sum, e) => sum + e.primeEmise, 0),
            annulation: emissions.reduce((sum, e) => sum + e.annulation, 0),
            primeNetteEmise: emissions.reduce((sum, e) => sum + e.primeNetteEmise, 0),
            cessionLegale: emissions.reduce((sum, e) => sum + e.cessionLegale, 0),
            cessionFacultative: emissions.reduce((sum, e) => sum + e.cessionFacultative, 0),
            cessionTotale: emissions.reduce((sum, e) => sum + e.cessionTotale, 0),
            primeNetteCedee: emissions.reduce((sum, e) => sum + e.primeNetteCedee, 0)
        };

        // Prepare table data
        const tableData = emissions.map(emission => [
            emission.compagnie,
            displayNumbers(emission.primeEmise),
            displayNumbers(emission.annulation),
            displayNumbers(emission.primeNetteEmise),
            displayNumbers(emission.cessionLegale),
            displayNumbers(emission.cessionFacultative),
            displayNumbers(emission.cessionTotale),
            displayNumbers(emission.primeNetteCedee),
            emission.tauxCession ? `${emission.tauxCession.toFixed(2)}%` : '0%'
        ]);

        // Add totals row
        tableData.push([
            'TOTAL',
            displayNumbers(totals.primeEmise),
            displayNumbers(totals.annulation),
            displayNumbers(totals.primeNetteEmise),
            displayNumbers(totals.cessionLegale),
            displayNumbers(totals.cessionFacultative),
            displayNumbers(totals.cessionTotale),
            displayNumbers(totals.primeNetteCedee),
            ''
        ]);

        // Add table
        pdf.autoTable({
            head: [[
                'COMPAGNIE',
                'PRIME EMISE',
                'ANNULATION',
                'PRIME NETTE\nEMISE',
                'CESSION\nLEGALE',
                'CESSION\nFACULTATIVE',
                'CESSION\nTOTALE',
                'PRIME NETTE\nCEDEE',
                'TAUX'
            ]],
            body: tableData,
            startY: 30,
            styles: {
                fontSize: 8,
                cellPadding: 2
            },
            headStyles: {
                fillColor: [36, 33, 102],
                textColor: 255,
                fontSize: 8,
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 40 },
                1: { cellWidth: 25, halign: 'right' },
                2: { cellWidth: 25, halign: 'right' },
                3: { cellWidth: 25, halign: 'right' },
                4: { cellWidth: 25, halign: 'right' },
                5: { cellWidth: 30, halign: 'right' },
                6: { cellWidth: 25, halign: 'right' },
                7: { cellWidth: 28, halign: 'right' },
                8: { cellWidth: 20, halign: 'center' }
            },
            didParseCell: function (data) {
                // Make total row bold
                if (data.row.index === tableData.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });

        const fileName = `Emissions_Nettes_${categorie}_${exercice}`;
        pdf.save(`${fileName}.pdf`);

        return true;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        return false;
    }
};